import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { TableCell } from '@anytag/shared-atoms';
import { SortAction } from '@anytag/shared-types';
import { formatIntNumber } from '@anytag/shared-utils';
import { usePostDetailsParam } from '@anytag/shared-hooks';
import { InfluencerSocialPost, PostListSortField, SocialPostType } from '@anytag/graphql';

interface Props extends SortAction<PostListSortField> {
  socialPostType: SocialPostType;
}

export const useInfluencerXhsPostsListTable = ({ socialPostType }: Props) => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();

  const columns: ColumnDef<InfluencerSocialPost>[] = [
    {
      accessorKey: 'post',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Post'),
      cell: ({ row }) => (
        <TableCell.Post
          postUrl={row.original.postUrl}
          title={row.original.caption || ''}
          thumbnail={row.original.thumbnail || ''}
          socialPostType={socialPostType}
          onClick={() => setPostDetailsParam(row.original.id)}
        />
      )
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.View'),
      meta: { align: 'right', sortField: PostListSortField.VIEW, help: t<string>('Tooltip.ViewHelp') },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.views)} />
    },
    {
      accessorKey: 'likes',
      header: t<string>('HeaderColumn.Like'),
      meta: { align: 'right', sortField: PostListSortField.LIKE, help: t<string>('Tooltip.LikeHelp') },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.likes)} />
    },
    {
      accessorKey: 'saved',
      header: t<string>('HeaderColumn.Save'),
      meta: { align: 'right', sortField: PostListSortField.SAVED, help: t<string>('Tooltip.SaveHelp') },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.saved)} />
    },
    {
      accessorKey: 'postType',
      header: t<string>('HeaderColumn.Post Type'),
      meta: { help: t<string>('Tooltip.PostTypeHelp') },
      cell: ({ row }) => (
        <TableCell.Text className="min-w-20" value={row.original.isSponsoredPost ? 'Sponsored Post' : 'Normal Post'} />
      )
    },
    {
      accessorKey: 'postDate',
      header: t<string>('HeaderColumn.Post date'),
      meta: { sortField: PostListSortField.POST_DATE, help: t<string>('Tooltip.PostDateHelp') },
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.date} />
    }
  ];

  return { columns };
};
